import { useEffect, useState } from "react";
import { BadgeCheck, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuthStore } from "@/lib/auth";
import {
  MEMBERSHIP_OPTIONS,
  NO_MEMBERSHIPS,
  parseMemberships,
  toggled,
  type Memberships,
} from "./membershipsView";

/** "I am an Avelo PLUS / Frontier Discount Den member" switches. Saved server-side, so they survive reload. */
export default function MembershipsCard() {
  const token = useAuthStore((s) => s.token);
  const [value, setValue] = useState<Memberships>(NO_MEMBERSHIPS);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<keyof Memberships | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/settings/memberships", { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setValue(parseMemberships(await res.json()));
      } catch {
        /* leave switches off; the user can still try */
      } finally {
        setLoaded(true);
      }
    })();
  }, [token]);

  async function flip(key: keyof Memberships) {
    const next = toggled(value, key);
    const prev = value;
    setValue(next);
    setBusy(key);
    setError(null);
    try {
      const res = await fetch("/api/settings/memberships", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: next[key] }),
      });
      if (!res.ok) throw new Error("save failed");
      setValue(parseMemberships(await res.json()));
    } catch {
      setValue(prev);
      setError("Could not save that. Please try again.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BadgeCheck className="h-5 w-5" /> Airline memberships
        </CardTitle>
        <CardDescription>Paid clubs you already belong to, so fares reflect what you actually pay.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {MEMBERSHIP_OPTIONS.map((o) => (
          <label
            key={o.key}
            className="flex items-start gap-3 border border-border rounded-lg p-3 cursor-pointer min-h-[44px]"
          >
            <input
              type="checkbox"
              role="switch"
              className="mt-1 h-5 w-5 shrink-0"
              checked={value[o.key]}
              disabled={!loaded || busy !== null}
              onChange={() => flip(o.key)}
              aria-label={`I have ${o.label}`}
            />
            <span className="min-w-0 flex-1">
              <span className="block font-medium">I have {o.label}</span>
              <span className="block text-xs text-muted-foreground">{o.blurb}</span>
            </span>
            {busy === o.key && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
          </label>
        ))}
        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      </CardContent>
    </Card>
  );
}
